/**
 * The page box, as a component rather than as a habit.
 *
 * Every area used to decide its own geometry. `App` chose between two stage
 * modes with a ternary on the area name; the SDR area built its own toolbar;
 * Replies wrapped itself in a pane with its own gutter, its own measure and its
 * own card padding. Three different shapes, picked by WHICH TAB you were on
 * rather than by what kind of content it held — so switching tabs moved the
 * content sideways and up, which is the complaint this file exists to answer.
 *
 * What is here is small on purpose. These are not layout components in the
 * sense of owning flexbox; they are named decisions. `Stage` decides whether a
 * surface scrolls or fills. `Panel` decides which of the two measures the
 * content reads at. `AreaToolbar` decides that a toolbar is one control tall
 * whatever happens to be in it. Each emits exactly the classes the stylesheet
 * already had, so adopting one changes no pixels — it removes the opportunity
 * to disagree.
 */

import type { ReactNode } from 'react';

/**
 * Which of the two page measures this surface reads at — §15.1.
 *
 * `form` is the reading measure: Settings, Admin, Enroll, anything taken in
 * top to bottom. `table` is the wide one: Accounts, Contacts, the Blocklist,
 * Replies. There is no third, and a screen that wants one is telling you it is
 * one of these two and has not decided which.
 */
export type Measure = 'form' | 'table';

export function Panel({
  measure = 'form',
  scroll = false,
  reveal = false,
  className,
  children,
}: {
  measure?: Measure;
  /**
   * The card takes the scrolling instead of the page — for a surface inside a
   * filled `Stage`, where the page itself cannot scroll.
   */
  scroll?: boolean;
  /**
   * Fade in on mount — for the card that replaces a `Loading`.
   *
   * Applied unconditionally rather than only when a loader was actually shown,
   * and that is a deliberate trade with a number behind it. The alternative
   * needs every call site to thread through whether its first paint came from
   * `api.peek`'s cache, and what it buys is 160ms on the cache-hit path — a
   * path that is already 200 to 450ms faster than it was, measured on
   * production. Cheap enough to pay; complicated enough not to plumb.
   *
   * The animation runs on MOUNT, not on render, so a revalidation that swaps
   * the rows underneath a card already on screen does not re-fade it. That is
   * the behaviour that matters and it comes for free.
   */
  reveal?: boolean;
  /** An area's own modifier. Geometry does not belong here. */
  className?: string;
  children: ReactNode;
}) {
  return (
    <section
      className={[
        'panel',
        measure === 'table' ? 'panel-wide' : null,
        scroll ? 'panel-scroll' : null,
        reveal ? 'reveal' : null,
        className,
      ]
        .filter(Boolean)
        .join(' ')}
    >
      {children}
    </section>
  );
}

/**
 * A surface's top row: what you are looking at on the left, what you can do
 * about it on the right.
 *
 * The height is the point. `.area-actions` centres its children, so a row
 * holding a chip AND a button was six pixels taller than a row holding only the
 * chip — and the Queue tab had an Enroll button while the Replies tab did not,
 * so everything below the toolbar moved on every switch between them. The
 * controls agree on `--control-h` now and the row reserves that height, so a
 * toolbar cannot change size because of what it happens to contain today.
 */
export function AreaToolbar({
  left,
  right,
}: {
  left?: ReactNode;
  right?: ReactNode;
}) {
  return (
    <div className="area-actions">
      {left}
      <div className="actions-right">{right}</div>
    </div>
  );
}

/**
 * The main region under the top bar, in one of its two modes.
 *
 * `scroll` — the page scrolls and the content sits on it at the shared gutter.
 * Every area except SDR.
 *
 * `fill` — the region is exactly the window and its children scroll
 * themselves. The SDR queue's two independent panes (§15.1) need this, and so
 * does anything else that wants its own bottom edge rather than the page's.
 *
 * It was a ternary on the area's NAME, which meant the mode was a property of
 * the tab rather than of the content, and nothing stopped a second area from
 * wanting `fill` and having to be added to the condition.
 */
export function Stage({
  variant = 'scroll',
  children,
}: {
  variant?: 'scroll' | 'fill';
  children: ReactNode;
}) {
  return (
    <main className={variant === 'fill' ? 'stage stage-flush' : 'stage'}>
      {children}
    </main>
  );
}

/**
 * What a surface shows while its first read is in flight.
 *
 * **A spinner, not a skeleton, and the reason is a promise.** The first pass at
 * this drew grey bars where the rows would be — and the owner's report on it
 * was exact: "sometimes it's narrower than the loaded table". That is what a
 * skeleton costs. Bars are a claim about the shape of what is coming, made at
 * the one moment nothing knows that shape, and the claim is wrong whenever the
 * table turns out wider, denser or shorter than the placeholder guessed. The
 * eye reads the bars as content, then has to re-read the real thing.
 *
 * A spinner claims nothing. It says "waiting", which is the only true statement
 * available, and it cannot disagree with what arrives.
 *
 * What it still has to do is RESERVE, because an unreserved loading state is
 * the layout jump this whole pass exists to remove. So the region holds a
 * height while it waits. It will not match the content exactly — nothing can
 * — but a centred mark in a held box settles into the content, where a wrong
 * skeleton has to be replaced by it.
 */
export function Loading({ reserve = 'list', label = 'Loading' }: { reserve?: Reserve; label?: string }) {
  return (
    <div className={`loading loading-${reserve}`} role="status" aria-live="polite">
      <span className="spinner" aria-hidden="true" />
      <span className="sr-live">{label}&hellip;</span>
    </div>
  );
}

/**
 * How much room a loading region holds while it waits.
 *
 * `page` — a whole area is arriving, usually behind a lazy chunk boundary as
 *          well as a request. Holds roughly a first screenful.
 * `list` — the card is already drawn and only its rows are missing. Holds
 *          about five rows' worth.
 * `inline` — a panel inside a page, a preview, a drawer section.
 */
export type Reserve = 'page' | 'list' | 'inline';

/** A whole area, waiting — the card plus the held region inside it. */
export function PanelLoading({ measure = 'form' }: { measure?: Measure }) {
  return (
    <Panel measure={measure}>
      <Loading reserve="page" />
    </Panel>
  );
}
